import React from "react";
import { Link } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import "./Dashboard.css";

/**
 * EntityCard - Reusable list card component for schools, organizations, etc.
 *
 * @param {Object} entity - Entity data with:
 *   - id: string|number
 *   - name: string
 *   - email: string
 *   - image_url: string (optional)
 * @param {Array} stats - Array of { label, value } for displaying stats
 * @param {string} viewLink - Link for View Profile button
 * @param {string} editLink - Link for Modify dropdown item
 * @param {Function} onDelete - Callback for delete action (id, name)
 * @param {boolean} deleteLoading - Loading state for delete
 * @param {string} viewLabel - Label for view button (default: "View Profile")
 * @param {Array} customActions - Additional dropdown actions [{ label, icon, onClick, className }]
 */
export const EntityCard = ({
  entity,
  stats = [],
  viewLink,
  editLink,
  onDelete,
  deleteLoading = false,
  viewLabel = "View Profile",
  customActions = [],
}) => {
  const getInitials = (name) => {
    if (!name) return "?";
    const words = name.split(" ");
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="entity-card">
      <div className="row align-items-center">
        {/* Logo */}
        <div className="col-auto">
          {entity.image_url ? (
            <img
              src={entity.image_url}
              alt={entity.name}
              className="entity-logo"
            />
          ) : (
            <div className="entity-logo-placeholder">
              {getInitials(entity.name)}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="col entity-info">
          <h3 className="entity-name">{entity.name}</h3>
          <p className="entity-email">
            <span>Email:</span> {entity.email}
          </p>
        </div>

        {/* Stats */}
        {stats.length > 0 && (
          <div className="col-auto d-none d-lg-block">
            <div className="entity-stats">
              {stats.map((stat, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <div className="entity-stat-divider"></div>}
                  <div className="entity-stat-item">
                    <p className="entity-stat-label">{stat.label}</p>
                    <p className="entity-stat-value">{stat.value}</p>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="col-auto">
          <div className="entity-actions">
            <Link
              to={viewLink}
              state={{ entityId: entity.id }}
              className="entity-view-btn"
            >
              {viewLabel}
            </Link>
            <Dropdown className="entity-dropdown">
              <Dropdown.Toggle as="button" className="entity-menu-btn">
                <i className="fa-solid fa-ellipsis-vertical"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                {editLink && (
                  <Dropdown.Item as={Link} to={editLink}>
                    <i className="fa-solid fa-pen"></i>
                    Modify
                  </Dropdown.Item>
                )}
                {customActions.map((action, index) => (
                  <Dropdown.Item
                    key={index}
                    className={action.className || ""}
                    onClick={action.onClick}
                  >
                    <i className={`fa-solid ${action.icon}`}></i>
                    {action.label}
                  </Dropdown.Item>
                ))}
                {onDelete && (
                  <Dropdown.Item
                    className="text-danger"
                    onClick={() => onDelete(entity.id, entity.name)}
                    disabled={deleteLoading}
                  >
                    <i className="fa-solid fa-trash"></i>
                    Delete
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntityCard;
